import { useSelector, useDispatch } from 'react-redux';

// Types
import { UserRole, OrganizationRole } from 'Shared/constants/types';
import { CurrentUser } from 'Shared/types/CurrentUser.type';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

/**
 * A hook which returns the current user and various properties about them.
 */
export const useCurrentUser = () => {
  const dispatch = useDispatch();
  const { organization } = useOrganization();

  const user: CurrentUser = useSelector((state: { user: CurrentUser }) => state.user);

  const isLoggedIn = user && Object.keys(user).length > 0 && Object.getPrototypeOf(user) === Object.prototype;
  const id = user.id;
  const slug = user.slug;
  const isManager = user.role === UserRole.manager;
  const isOrgAdmin = user.organizationRole === OrganizationRole.admin;
  const avatar = user.avatar;
  const isPiUser = !!user.pi_user_guid;
  const isImpersonated = user.isImpersonated;
  const hasCompletedOnboarding = !!user?.onboarding?.completed_at;
  const isThirdPartyUser = !user?.selectedMembership?.primary;
  const isBillingContact = organization?.billingContact?.id === id;

  const getFlag = ({ flag }: { flag: string }) => {
    if (!user) return null;
    if (!user.flags) return false;
    return user.flags[flag];
  };

  const updateFlag = ({ flag, value }: { flag: string; value }) => {
    if (!flag || !value) return null;
    return dispatch(UsersActions.updateUserFlag(flag, value)).then(response => {
      return response;
    });
  };

  return {
    user,
    hasCompletedOnboarding,
    avatar,
    isLoggedIn,
    id,
    slug,
    isManager,
    isImpersonated,
    isOrgAdmin,
    isPiUser,
    isThirdPartyUser,
    getFlag,
    updateFlag,
    hasAsanaIntegration: user.hasAsanaIntegration,
    hasJiraIntegration: user.hasJiraIntegration,
    piUserGuid: user?.pi_user_guid,
    isBillingContact,
  };
};
